import {
  getGetMeQueryKey,
  useGetInstanceInfo,
  useGetMe,
  useListPoints,
  useLogoutStaff,
  useSelectCurrentPoint,
} from '@postifoto/api-client';
import { useQueryClient } from '@tanstack/react-query';
import { Link, Outlet, useNavigate } from '@tanstack/react-router';
import { useTranslations } from 'use-intl';
import { can, canGlobal, myPoints } from '../auth/session';
import { StepUpProvider } from '../auth/step-up';
import { Brand } from '../components/ui/brand';
import { Button } from '../components/ui/button';
import { Select } from '../components/ui/input';

const navLink =
  'flex h-9 shrink-0 items-center whitespace-nowrap rounded-md border border-transparent px-2.5 text-sm font-medium text-body hover:bg-surface-card hover:text-ink data-[status=active]:border-hairline data-[status=active]:bg-canvas data-[status=active]:text-ink data-[status=active]:shadow-sm';

/**
 * Оболочка приложения: боковая панель (инстанс, рабочая точка, меню по правам, пользователь).
 * Уже 1024px панель становится верхней полосой. Пункты меню — только удобство: права проверяет API.
 */
export function AppShell() {
  const t = useTranslations();
  const instance = useGetInstanceInfo();
  const me = useGetMe();
  const points = useListPoints();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const select = useSelectCurrentPoint({
    mutation: { onSuccess: () => queryClient.invalidateQueries({ queryKey: getGetMeQueryKey() }) },
  });
  const logout = useLogoutStaff({
    mutation: {
      onSettled: async () => {
        queryClient.clear();
        await navigate({ to: '/login' });
      },
    },
  });

  const mine = myPoints(me.data);
  const available = (points.data?.items ?? []).filter(
    (point) => point.active && (mine === 'all' || mine.has(point.id)),
  );
  const admin = [
    { to: '/catalog', label: t('shell.catalog'), show: can(me.data, 'catalog.manage') },
    {
      to: '/pricing',
      label: t('shell.pricing'),
      show: can(me.data, 'pricing.manage') || can(me.data, 'order.create'),
    },
    {
      to: '/scheduling',
      label: t('shell.scheduling'),
      show:
        can(me.data, 'catalog.manage') ||
        canGlobal(me.data, 'points.manage') ||
        can(me.data, 'order.create'),
    },
    {
      to: '/imports',
      label: t('shell.imports'),
      show: can(me.data, 'pricing.manage') || can(me.data, 'customer.edit'),
    },
    { to: '/users', label: t('shell.users'), show: canGlobal(me.data, 'users.manage') },
    {
      to: '/roles',
      label: t('shell.roles'),
      show: canGlobal(me.data, 'roles.manage') && canGlobal(me.data, 'users.manage'),
    },
    { to: '/points', label: t('shell.points'), show: true },
    { to: '/audit', label: t('shell.audit'), show: can(me.data, 'audit.view') },
    { to: '/settings', label: t('shell.settings'), show: canGlobal(me.data, 'settings.manage') },
  ] as const;

  return (
    <StepUpProvider>
      <div className="grid min-h-screen lg:grid-cols-[240px_minmax(0,1fr)]">
        <aside className="flex flex-wrap items-center gap-3 border-b border-hairline px-4 py-2 lg:sticky lg:top-0 lg:h-screen lg:flex-col lg:flex-nowrap lg:items-stretch lg:gap-6 lg:border-r lg:border-b-0 lg:py-4">
          <Brand name={instance.data?.name ?? ''} logoUrl={instance.data?.brand.logoUrl} />
          {available.length > 0 && (
            <label
              htmlFor="current-point"
              className="order-last grid w-full gap-1 text-xs text-muted sm:order-none sm:w-auto lg:w-full"
            >
              {t('shell.currentPoint')}
              <Select
                id="current-point"
                value={me.data?.session.currentPointId ?? ''}
                onChange={(event) =>
                  select.mutate({ data: { pointId: event.target.value || null } })
                }
              >
                <option value="">{t('shell.noPoint')}</option>
                {available.map((point) => (
                  <option key={point.id} value={point.id}>
                    {point.name}
                  </option>
                ))}
              </Select>
            </label>
          )}
          <nav
            aria-label={t('shell.navLabel')}
            className="order-last flex w-full gap-1 overflow-x-auto lg:order-none lg:grid lg:gap-0.5 lg:overflow-visible"
          >
            <Link to="/" activeOptions={{ exact: true }} className={navLink}>
              {t('shell.status')}
            </Link>
            {can(me.data, 'order.view') && (
              <Link to="/orders" className={navLink}>
                {t('shell.orders')}
              </Link>
            )}
            {can(me.data, 'cash.open') && (
              <Link to="/cash" className={navLink}>
                {t('shell.cash')}
              </Link>
            )}
            {(can(me.data, 'transfer.send') ||
              can(me.data, 'transfer.receive') ||
              can(me.data, 'order.issue')) && (
              <Link to="/scan" className={navLink}>
                {t('shell.scan')}
              </Link>
            )}
            {can(me.data, 'production.queue.view') && (
              <Link to="/production" className={navLink}>
                {t('shell.production')}
              </Link>
            )}
            {can(me.data, 'customer.view') && (
              <Link to="/customers" className={navLink}>
                {t('shell.customers')}
              </Link>
            )}
            {admin.some((item) => item.show) && (
              <span className="hidden px-2.5 pt-2 pb-1 text-xs font-medium text-muted lg:block">
                {t('shell.adminGroup')}
              </span>
            )}
            {admin
              .filter((item) => item.show)
              .map((item) => (
                <Link key={item.to} to={item.to} className={navLink}>
                  {item.label}
                </Link>
              ))}
          </nav>
          <div className="ml-auto flex items-center gap-2 lg:mt-auto lg:ml-0 lg:justify-between lg:border-t lg:border-hairline lg:px-2 lg:pt-3">
            <Link
              to="/profile"
              className="group min-w-0 text-xs text-muted"
              title={t('shell.profile')}
            >
              <strong className="block truncate text-sm font-medium text-ink group-hover:underline group-hover:underline-offset-3">
                {me.data?.user.displayName}
              </strong>
              <span className="hidden truncate lg:block">{me.data?.user.email}</span>
            </Link>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => logout.mutate()}
              disabled={logout.isPending}
            >
              {t('shell.logout')}
            </Button>
          </div>
        </aside>
        <main className="min-w-0 px-4 py-6 lg:px-8 lg:py-8">
          <div className="mx-auto max-w-content">
            <Outlet />
          </div>
        </main>
      </div>
    </StepUpProvider>
  );
}
